<template>
  <stihl-section id="secMFA" :title="$t('dss.page.security.secMFA')" class="mt-5">

    <p class="form-text">{{ $t('dss.page.security.2fa.info') }}</p>

    <div>{{ $t('dss.page.security.2fa.availableMethods') }}</div>
    <ul id="mfa" class="list-group">

      <li class="list-group-item">
        <div>{{ $t('dss.page.security.2fa.method.email') }}</div>
        <div class="form-text">{{ $t('dss.page.security.2fa.method.emailDesc') }}</div>
      </li>

      <li class="list-group-item">
        <div class="d-flex justify-content-between">
          <span>{{ $t('dss.page.security.authenticator') }}</span>
          <img id="oathDelete" class="icon"
               src="/assets/icons/trash.svg" alt="delete"
               @click="state='showConfirmDeleteModal'" v-if="hasOathDevice"/>
        </div>

        <div class="form-text" v-if="lastUsed">
          {{ $t('dss.page.security.lastUse', [localeDateFormat(lastUsed, i18n.global.locale)]) }}
        </div>
        <span class="form-text" v-if="!hasOathDevice">{{ $t('dss.page.security.noDevice') }}</span>
      </li>

    </ul>

    <div class="buttons" v-if="!hasOathDevice">
      <button id="oathAdd" class="btn btn-ghost" @click="addDevice" v-if="state!=='adding'">
        <img class="btn-icon" src="/assets/icons/plus.svg" alt="configure"/>
        <span>{{ $t('dss.page.security.2fa.method.oath.add.button') }}</span>
      </button>
    </div>

    <confirmation-modal id="oathConfirmDelete" :title="$t('dss.page.security.reallyDeleteDevice')"
                        :confirm="$t('dss.button.confirmDelete')"
                        @confirm="deleteDevice" @cancel="state=''"
                        v-if="state === 'showConfirmDeleteModal'"/>

    <stihl-modal id="oathModal" class="modal-lg" @modal-closed="cancelAddDevice"
                 v-if="state === 'showAddModal' || state === 'adding'">
      <stihl-overlay :active="state==='adding'">
        <oath-registration-verifier :otp-auth="otpAuth" @verify:code="verifyCode"/>
      </stihl-overlay>
    </stihl-modal>

  </stihl-section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import FormGroup from '@/components/ui/FormGroup.vue';
import StihlSection from '@/components/ui/StihlSection.vue';
import ConfirmationModal from '@/components/ui/ConfirmationModal.vue';
import StihlModal from '@/components/ui/StihlModal.vue';
import { localeDateFormat } from '@/components/utils';
import * as forgerock from '@forgerock/javascript-sdk';
import {
  CallbackType, FRCallback, FRLoginFailure, FRLoginSuccess, StepOptions,
  StepType, FRStep
} from '@forgerock/javascript-sdk';
import OathRegistrationVerifier from './OathRegistrationVerifier.vue';
import StihlOverlay from '@/components/ui/StihlOverlay.vue';
import Backend, { UserDTO } from '@/components/backend';
import { i18n } from '@/lang';

export default defineComponent({
  name: 'MfaSection',
  components: { StihlOverlay, OathRegistrationVerifier, StihlModal, ConfirmationModal, StihlSection, FormGroup },

  props: {
    user: { type: Object as PropType<UserDTO>, required: true },
  },

  data() {
    return {
      lastUsed: this.user.lastOathUse,
      state: '' as '' | 'showAddModal' | 'adding' | 'showConfirmDeleteModal' | 'deleting',
      otpAuth: '',
      currentStep: undefined as FRStep | undefined,
    };
  },

  computed: {
    i18n: () => (i18n),
    hasOathDevice() {
      return !!this.lastUsed;
    },
  },

  methods: {
    localeDateFormat,

    async deleteDevice() {
      if (this.state === 'deleting') return; // already deleting - wait

      this.state = 'deleting';

      const backendAPI = await Backend.validateToken();
      try {
        await backendAPI.deleteOathDevice();
        this.lastUsed = '';
        this.$toast.info(this.$t('dss.page.security.oathDelete.success'));
      } catch (e) {
        this.$toast.error(this.$t('dss.page.security.oathDelete.error'));
      }

      this.state = '';
    },

    addDevice() {
      console.debug('[oath:add] start');
      // start the 'enroll_oath_device' auth-tree
      this.nextStep(undefined, {
        query: {
          'ForceAuth': 'true', // force authentication
          authIndexType: 'service',
          authIndexValue: import.meta.env.VITE_REGISTER_DEVICE_TREE,
        }
      });
    },

    async nextStep(step?: FRStep, opts?: StepOptions) {
      console.debug(`[oath:add] submit ${step?.type} [stage:${step?.payload?.stage}]`);
      try {

        await forgerock.FRAuth.next(step, opts).then(async (step: FRStep | FRLoginSuccess | FRLoginFailure) => {
          console.log(`[oath:add] new-step: ${step.type} [stage:${step.payload.stage}]`);

          if (step.type === StepType.LoginSuccess) {
            this.lastUsed = new Date().toISOString();
            this.$toast.info(this.$t('dss.page.security.oathAdd.success'));
            this.state = ''; // hide the modal
            this.currentStep = undefined; // cleanup
            return true;
          }

          if (step.type === StepType.LoginFailure) {
            this.$toast.error(this.$t('dss.page.security.oathAdd.error'));
            this.state = ''; // hide the modal
            this.currentStep = undefined; // cleanup
            return false;
          }

          const stage = step.getStage();
          switch (stage) {
            case 'OATH-Registration1':
              return this.handleStep1(step);

            case 'OATH-Registration2':
              return this.handleStep2(step);
          }

          throw new Error('Unknown stage <' + stage + '>');

        });
      } catch (err) {
        console.error('[oath:add] catch', err);
        this.$toast.error(this.$t('dss.page.security.oathAdd.error'));
      }
    },

    /**
     * 1. stage of the 'enroll_oath_device' auth-tree - save otpAuth and submit to go to the next step automatically
     */
    handleStep1(currentStep: FRStep) {
      console.debug('[oath:add] handleStep1');
      // make sure we are in the correct stage
      if (currentStep.getStage() !== 'OATH-Registration1') throw new Error('[OATH-Registration1] invalid stage');

      // recover otpAuth value and save it in the store
      const _otpAuth = currentStep.callbacks
          ?.find((c: FRCallback) => c.getType() === CallbackType.HiddenValueCallback
              && c.getOutputValue('id') === 'mfaDeviceRegistration')
          ?.getOutputValue('value');
      if (!_otpAuth) throw new Error('[OATH-Registration1] otpAuth missing');
      this.otpAuth = _otpAuth as string;

      // go to the next step
      this.nextStep(currentStep);
    },

    /**
     * 2. stage of the 'enroll_oath_device' auth-tree - show the modal-page
     */
    handleStep2(step: FRStep) {
      console.debug('[oath:add] handleStep2');
      // if currentStep was already set, it means we return to the verification stage, because the code was wrong
      if (this.currentStep) this.$toast.error(this.$t('dss.page.security.oathAdd.badCode'));

      this.currentStep = step; // save the current step
      this.state = 'showAddModal'; // show the modal page to enter the code and verify
    },

    verifyCode(code: string) {
      console.debug('[oath:add] verifyCode');
      if (this.currentStep?.getStage() !== 'OATH-Registration2') throw new Error('[OATH-Registration2] invalid stage');

      // fill TOTP code
      this.currentStep.getCallbackOfType(CallbackType.NameCallback).setInputValue(code);
      // fill submit-type: 0 - submit code for verification & device-registration (skip not necessary here)
      this.currentStep.getCallbackOfType(CallbackType.ConfirmationCallback).setInputValue(0);

      this.state = 'adding';
      this.nextStep(this.currentStep as FRStep);
    },

    cancelAddDevice() {
      console.debug('[oath:add] cancel');
      this.state = '';
      this.currentStep = undefined;
    },
  }
});
</script>

